<script setup>
    import { useStorageAsync, useMediaQuery, useDebounceFn } from '@vueuse/core';
    import { useHeroAltLogo } from '@/composables/useHeroAltLogo';

    import SessionsGridList from '~/components/blocks/two-six/sessions/grid-list.vue';
    import SessionsCalendarList from '~/components/blocks/two-six/sessions/calendar-list.vue';
    import SessionsAssistant from '~/components/blocks/two-six/sessions/assistant.vue';

    // Dark "alt" header logo — applied only when this block is index 0 (the hero
    // directly under the header), never when it sits below another hero.
    const rootRef = ref(null);
    useHeroAltLogo(rootRef);

    const { loggedIn, user, fetch: refreshSession } = useUserSession();
    const isTablet = useMediaQuery('(min-width: 768px)', { ssrWidth: 768 });

    import { useRouteQuery } from '@vueuse/router';
    import { scrollIntoView } from '@/composables/helpers/client-only/scroll';
    import { date_options, skill_level_options, date_options_cal, seating_options, availability_options } from '~~/shared/utilities/schedule';

    const { hasConferenceEnded } = useConferenceStatus();

    const props = defineProps({
        headline: {
            type: String,
            default: '',
        },
        copy: {
            type: String,
            default: '',
        },
        year: {
            type: Number,
            default: 2025,
        },
        unscheduled: {
            type: Boolean,
            default: false,
        },
        showAvailability: {
            type: Boolean,
            default: false,
        },
        enableFavorites: {
            type: Boolean,
            default: false,
        },
        treatAsHero: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableCalendar: {
            type: Boolean,
            default: false,
        },
        enableSaved: {
            type: Boolean,
            default: false,
        },
        enableAssistant: {
            type: Boolean,
            default: false,
        },
        contactId: {
            type: String,
            default: '',
        },
        tabs: {
            type: Boolean,
            default: true,
        },
    });

    const { $bus, $clientPosthog } = useNuxtApp();

    const route = useRoute();
    const showReservationBanner = import.meta.server ? ref(null) : useStorageAsync('showReservationBanner', null);

    const tab = useRouteQuery('tab', 'sessions');
    const view = useRouteQuery('view', 'list');
    const time_range_val = useRouteQuery('time_range', [0, 100]);
    const page = useRouteQuery('page', 1);
    // Internal loading cursor. URL `page` is a UI-position marker updated from
    // scroll; loadingPage drives the actual fetch sequence. Seeded from URL so
    // SSR renders the requested page; client mount backfills earlier pages.
    const loadingPage = ref(parseInt(page.value, 10) || 1);
    const availability = useRouteQuery('availability', 'all');
    const day = useRouteQuery('day', 'all');
    const level = useRouteQuery('level', 'all');
    const tags = useRouteQuery('tags', []);
    const ended = useRouteQuery('ended', false);
    const seating = useRouteQuery('seating', 'all');
    const assistantHelp = useRouteQuery('help', false);

    const PER_PAGE = 15;

    const dayjs = useDayjs();
    const showTags = ref(false);
    const showSearch = ref(false);
    const grid = ref(null);
    const refreshTime = ref(null);

    const dates = computed(() => (view.value === 'calendar' ? date_options_cal : date_options));
    const skill_levels = ref(skill_level_options);
    const all_seating_options = ref(seating_options);

    const tab_options = ref([
        {
            label: 'All Sessions',
            value: 'sessions',
        },
        {
            label: 'My Favorites',
            value: 'favorites',
        },
        {
            label: 'Saved Seats',
            value: 'seats',
        },
        {
            label: 'Assistant',
            value: 'assistant',
        },
    ]);

    const start_hour = 9;
    const controls = ref(null);

    const activeTags = computed(() => {
        const tagsValue = Array.isArray(tags.value) ? tags.value : tags.value ? tags.value.split(',') : [];
        return new Set(tagsValue) || new Set();
    });

    const removeTag = (tag) => {
        const current = Array.isArray(tags.value) ? [...tags.value] : tags.value ? tags.value.split(',') : [];
        tags.value = current.filter(t => t !== tag);
    };

    watch(view, (newView, oldView) => {
        if (oldView === 'list' && newView !== 'list') {
            day.value = '2025-09-03';
        }
        if (newView !== oldView) {
            $clientPosthog?.capture('agenda_view_changed', { view: newView, previous_view: oldView });
        }
    });

    watch(tab, (newTab, oldTab) => {
        if (newTab !== oldTab) {
            $clientPosthog?.capture('agenda_tab_changed', { tab: newTab, previous_tab: oldTab, is_authenticated: loggedIn.value });
        }
    });

    watch(day, (newDay, oldDay) => {
        if (newDay !== oldDay) {
            $clientPosthog?.capture('agenda_filter_day_changed', { day: newDay, previous_day: oldDay });
        }
    });

    watch(level, (newLevel, oldLevel) => {
        if (newLevel !== oldLevel) {
            $clientPosthog?.capture('agenda_filter_level_changed', { skill_level: newLevel, previous_level: oldLevel });
        }
    });

    watch(seating, (newSeating, oldSeating) => {
        if (newSeating !== oldSeating) {
            $clientPosthog?.capture('agenda_filter_seating_changed', { seating: newSeating, previous_seating: oldSeating });
        }
    });

    watch(availability, (newAvail, oldAvail) => {
        if (newAvail !== oldAvail) {
            $clientPosthog?.capture('agenda_filter_availability_changed', { availability: newAvail, previous_availability: oldAvail });
        }
    });

    const trackTimeRangeChange = useDebounceFn((newRange, oldRange) => {
        $clientPosthog?.capture('agenda_filter_time_range_changed', {
            time_range_start: newRange[0],
            time_range_end: newRange[1],
            previous_time_range_start: oldRange[0],
            previous_time_range_end: oldRange[1],
        });
    }, 500);

    watch(
        time_range_val,
        (newRange, oldRange) => {
            if (JSON.stringify(newRange) !== JSON.stringify(oldRange)) {
                trackTimeRangeChange(newRange, oldRange);
            }
        },
        { deep: true }
    );

    const params = computed(() => {
        const favorites = loggedIn.value && user.value && user.value.favorites?.length ? user.value.favorites.join(',') : true;
        const reservations = loggedIn.value && user.value && user.value.sessionRegistration?.length ? user.value.sessionRegistration.join(',') : true;

        return {
            year: props.year || null,
            unscheduled: props.unscheduled || null,
            page: loadingPage.value,
            per_page: PER_PAGE,
            filter_tags: activeTags.value.size ? [...activeTags.value].join(',') : null,
            availability: availability.value === 'all' ? null : availability.value,
            day: day.value === 'all' ? null : day.value,
            skill_level: level.value === 'all' ? null : level.value,
            time_range_start: Array.isArray(time_range_val.value) ? time_range_val.value[0] : 0,
            time_range_end: Array.isArray(time_range_val.value) ? time_range_val.value[1] : 100,
            seating: seating.value === 'all' ? null : seating.value,
            order_by: 'day',
            view: view.value,
            favorites: tab.value === 'favorites' ? favorites : null,
            seats: tab.value === 'seats' ? reservations : null,
            assistant: tab.value === 'assistant' ? true : null,
            contact: props.contactId || null,
            user: loggedIn.value && (tab.value === 'favorites' || tab.value === 'seats') ? user.id : null,
            ts: loggedIn.value && (tab.value === 'favorites' || tab.value === 'seats') ? Date.now() : null,
        };
    });

    const button = {
        label: 'Learn More',
        url: '/help#reservations',
        title: 'Learn More',
        target: '_self',
    };

    // Key the cached payload to the year so that navigating between two
    // pages that both render this block (e.g. /agenda → /2025-sessions)
    // doesn't reuse the previous page's results — useAsyncData
    // deduplicates globally by key.
    const dataKey = `sessions-full-grid-${props.year || 'all'}`;
    const { data, pending, refresh } = useAsyncData(dataKey, () => $fetch('/api/v1/sessions', { params: { ...params.value } }), {
        watch: [loadingPage, tags, time_range_val, day, level, view, tab, loggedIn, refreshTime, seating, availability],
    });

    // Infinite scroll — accumulate sessions in buckets keyed by page number.
    // Computed output sorts buckets by page so backfills from deep-linked URLs
    // (e.g. ?page=3 SSRs page 3 first, then client prepends pages 1-2) render
    // in correct source order.
    const pageBuckets = ref({});
    const loadingMore = ref(false);

    watch(() => data.value?.sessions, (newSessions) => {
        if (!newSessions) {
            loadingMore.value = false;
            return;
        }
        const cp = parseInt(data.value?.current_page || loadingPage.value, 10) || 1;
        pageBuckets.value = {
            ...pageBuckets.value,
            [cp]: newSessions.map(s => ({ ...s, _page: cp })),
        };
        loadingMore.value = false;
    }, { immediate: true });

    const accumulatedSessions = computed(() => {
        const keys = Object.keys(pageBuckets.value).map(Number).sort((a, b) => a - b);
        const out = [];
        for (const k of keys) out.push(...pageBuckets.value[k]);
        return out;
    });

    const gridData = computed(() => {
        if (!data.value) return null;
        return {
            ...data.value,
            sessions: accumulatedSessions.value,
        };
    });

    // Initialize cascading=true if URL has page>1, so the observer can't advance
    // loadingPage during SSR hydration while the backfill flow is getting ready.
    const cascading = ref((parseInt(page.value, 10) || 1) > 1);

    const handleLoadMore = () => {
        if (cascading.value || loadingMore.value || pending.value) return;
        if (!data.value) return;
        const current = parseInt(loadingPage.value, 10) || 1;
        const total = parseInt(data.value.pages, 10) || 1;
        if (current < total) {
            loadingMore.value = true;
            loadingPage.value = current + 1;
        }
    };

    const handleClose = () => {
        showReservationBanner.value = false;
    };

    watch([time_range_val, day, level, tab, tags, view, availability], (newVals, oldVals) => {
        // Only trigger if any value has actually changed
        if (JSON.stringify(newVals) !== JSON.stringify(oldVals)) {
            pageBuckets.value = {};
            loadingPage.value = 1;
            page.value = 1;
            scrollIntoView(controls.value, { behavior: 'smooth', block: 'start' });
        }
    });

    const getTagNameById = (tagId) => {
        if (!data?.value?.all_tags) return '';
        const tag = data.value.all_tags.find((tag) => tag.id === tagId);
        return tag ? tag.name : '';
    };

    const startTime = computed(() => {
        const percent = Array.isArray(time_range_val.value) ? time_range_val.value[0] : 0;
        // 9 hours * 4 = 36 increments of 15 minutes
        const totalMinutes = (percent / 100) * (9 * 60);
        const hoursToAdd = Math.floor(totalMinutes / 60);
        const minutesToAdd = Math.round((totalMinutes % 60) / 15) * 15;
        const date = dayjs()
            .tz('America/Los_Angeles')
            .hour(start_hour + hoursToAdd)
            .minute(minutesToAdd)
            .second(0)
            .millisecond(0);
        return date.format('h:mma');
    });

    const endTime = computed(() => {
        const percent = Array.isArray(time_range_val.value) ? time_range_val.value[1] : 100;
        // 9 hours * 4 = 36 increments of 15 minutes
        const totalMinutes = (percent / 100) * (9 * 60);
        const hoursToAdd = Math.floor(totalMinutes / 60);
        const minutesToAdd = Math.round((totalMinutes % 60) / 15) * 15;
        const date = dayjs()
            .tz('America/Los_Angeles')
            .hour(start_hour + hoursToAdd)
            .minute(minutesToAdd)
            .second(0)
            .millisecond(0);
        return date.format('h:mma');
    });

    const results = computed(() => {
        if (!data.value) return '';
        return `${data.value.total} Results`;
    });

    const groupby = computed(() => {
        if (tab.value === 'assistant' && day.value === 'all') return 'day';
        return 'location';
    });

    const handlePageChange = (newPage) => {
        $clientPosthog?.capture('agenda_page_changed', {
            page: newPage,
            previous_page: page.value,
            total_results: data.value?.total,
        });
        page.value = newPage;
        refresh(dataKey);
        scrollIntoView(controls.value, { behavior: 'smooth', block: 'start' });
    };

    const handleReservationsLearnMoreClick = () => {
        $clientPosthog?.capture('agenda_reservations_learn_more_clicked');
        navigateTo(`/help#reservations`);
    };

    const handleLoginClick = async () => {
        $clientPosthog?.capture('agenda_login_prompt_clicked', {
            context: tab.value,
        });
        await navigateTo('/login');
    };

    const handleSearchClick = async () => {
        $clientPosthog?.capture('agenda_search_clicked');
        await navigateTo('/search?search=sessions');
    };

    const handleHelpClick = async () => {
        assistantHelp.value = true;
    };

    const handleFilterDrawerToggle = () => {
        showTags.value = !showTags.value;
    };

    const shareLink = computed(() => {
        if (user.value?.contactId && process.client) {
            const rootDomain = window.location.origin;
            return `${rootDomain}/favorites/${user.value.contactId}`;
        }
        return null;
    });

    $bus.on('auth:login', async () => {
        await refreshSession();
        if (tab.value === 'seats') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    $bus.on('sessions:registered', async () => {
        await refreshSession();
        if (tab.value === 'seats') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    $bus.on('sessions:unregistered', async () => {
        await refreshSession();
        if (tab.value === 'seats') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    watch(
        () => loggedIn,
        async () => {
            // await refreshSession();
            if (loggedIn.value) {
                refreshTime.value = Date.now();
                setTimeout(() => refresh(dataKey), 250);
            }
        },
        { immediate: true }
    );

    // Track scroll to update URL page marker based on topmost visible card
    let scrollTicking = false;
    const onScrollForUrl = () => {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
            scrollTicking = false;
            // Pick the topmost card whose top is at or past a small offset
            const offset = 120;
            const cards = document.querySelectorAll('.sessions-grid li[data-page]');
            let currentPage = 1;
            for (const li of cards) {
                const rect = li.getBoundingClientRect();
                if (rect.top >= offset - 10) break;
                const p = parseInt(li.getAttribute('data-page') || '1', 10);
                if (p > currentPage) currentPage = p;
            }
            if (currentPage === 1 && page.value !== 1) {
                page.value = 1;
            } else if (currentPage > 1 && parseInt(page.value, 10) !== currentPage) {
                page.value = currentPage;
            }
        });
    };

    const waitForPage = (n) => new Promise((resolve) => {
        if (parseInt(data.value?.current_page, 10) === n) return resolve();
        const stop = watch(() => data.value?.current_page, (cp) => {
            if (parseInt(cp, 10) === n) {
                stop();
                resolve();
            }
        });
        setTimeout(() => { stop(); resolve(); }, 5000);
    });

    // Backfill earlier pages behind an SSR'd deep-linked page (e.g. ?page=3 SSRs
    // page 3, then on client mount we fetch pages 1 and 2 to prepend). After
    // backfill, scroll to the first card of the target page so the user lands
    // where they expected.
    const backfillFromPage = async (target) => {
        if (target <= 1) return;
        cascading.value = true;
        // Fetch earlier pages directly via $fetch to avoid fighting useLazyAsyncData's
        // watcher, which cancels in-flight requests when loadingPage changes rapidly.
        for (let p = 1; p < target; p++) {
            if (pageBuckets.value[p]) continue;
            try {
                const result = await $fetch('/api/v1/sessions', { params: { ...params.value, page: p } });
                if (result?.sessions) {
                    pageBuckets.value = {
                        ...pageBuckets.value,
                        [p]: result.sessions.map(s => ({ ...s, _page: p })),
                    };
                }
            } catch (e) {
                console.error('[archive] backfill fetch failed for page', p, e);
            }
        }
        cascading.value = false;
        await nextTick();
        const firstCard = document.querySelector(`.sessions-grid li[data-page="${target}"]`);
        if (firstCard) {
            const y = firstCard.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'auto' });
        }
    };

    onMounted(() => {
        const targetPage = parseInt(page.value, 10) || 1;
        if (targetPage > 1) backfillFromPage(targetPage);
        window.addEventListener('scroll', onScrollForUrl, { passive: true });
        setTimeout(() => {
            if (showReservationBanner.value === null) showReservationBanner.value = 'true';
        }, 1000);
    });

    onUnmounted(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', onScrollForUrl);
        }
    });
</script>
<template>
    <div ref="rootRef" class="block-sessions-full-grid relative overflow-hidden" :class="{ 'is-hero': treatAsHero }">
        <img src="/img/2026/archive/hero-shard.png"
            alt="" aria-hidden="true"
            class="hero-shard hidden lg:display-block absolute top-0 right-0 w-[678px] h-auto pointer-events-none z-0" />
        <div class="relative z-[1] px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)]" ref="container">
            <slot name="header">
                <ProjectBreadcrumbs v-if="treatAsHero" light />
                <div class="text-[var(--magenta)] font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] mb-[30px] lg:mb-[60px]">
                    <h1 v-html="headline" class="font-hubspot-serif font-normal mb-[1.125rem] text-[36px] leading-[1.1] lg:text-[72px]"></h1>
                    <div v-html="copy" v-if="copy" class="max-w-[60rem] mb-0 copy"></div>
                </div>
            </slot>

            <div class="controls" ref="controls" :class="{ 'is-cal': view === 'calendar', 'is-day': day !== 'all', 'has-tabs': tabs }">
                <div class="flex flex-wrap items-center gap-[1.4375rem] mb-[1.3125rem] lg:mb-[2rem]" v-if="tab === 'sessions' || tab === 'favorites' || tab === 'seats'">
                    <div v-if="showAvailability" class="hidden lg:flex lg:items-center lg:gap-[1.4375rem]">
                        <USelect v-model="availability" :items="availability_options" class="availability-select" variant="outline" :ui="{ content: 'ring-0 font-hubspot-serif text-white', item: 'font-hubspot-serif text-white' }" />
                    </div>
                    <UButton
                        color="primary"
                        variant="outline"
                        icon="local-filter"
                        @click="handleFilterDrawerToggle"
                        class="more-filters"
                        :aria-expanded="showTags"
                        :aria-controls="showTags ? 'session-tags-drawer' : null"
                        label="Show Session Filters">
                        Filter by Topic
                        <template v-if="activeTags.size > 0">({{ activeTags.size }})</template>
                    </UButton>
                    <ProjectSearchButton v-if="tab === 'sessions'" class="search-button" @click="handleSearchClick" />
                    <p
                        class="hidden lg:display-block text-[12px] text-[var(--magenta)] font-hubspot-sans font-normal uppercase tracking-[0.84px] leading-[1.5] whitespace-nowrap ml-auto"
                        v-if="data && data.total && view !== 'calendar'">
                        {{ results }}
                    </p>
                </div>
                <div class="mb-[1.3125rem]">
                    <div v-if="data?.all_tags && activeTags.size && data?.sessions?.length" class="mb-[2.5rem] lg:mb-[2rem]">
                        <ul class="flex flex-row flex-wrap gap-[1rem]">
                            <li v-for="tag in activeTags" :key="tag">
                                <ProjectTag
                                    :text="getTagNameById(tag)"
                                    ariaText="Remove Tag"
                                    colorSet="white"
                                    :useHover="true"
                                    :useX="true"
                                    @click="removeTag(tag)"
                                    gtmEventName="session_filter_tag_clicked" />
                            </li>
                            <li class="clear-all">
                                <ProjectTag text="Clear Topics" colorSet="clear-all" :useHover="true" @click="tags = []" />
                            </li>
                        </ul>
                    </div>
                    <slot name="share">
                        <div v-if="tab === 'favorites' && loggedIn" class="share">
                            <FragmentsShare label="Share My Favorites" description="My favorites for INBOUND" :link="shareLink" :copy-to-clipboard="true" />
                        </div>
                    </slot>
                </div>
            </div>
            <template v-if="(gridData && gridData.sessions?.length) || (data && data.sessions?.length)">
                <SessionsGridList
                    v-if="view === 'list'"
                    :data="gridData || data"
                    :loading="loadingMore"
                    :limit="PER_PAGE"
                    compact
                    infinite
                    ref="grid"
                    @changePage="handlePageChange"
                    @loadMore="handleLoadMore"
                    id="grid"
                    :results_count="results" />
                <SessionsCalendarList
                    v-if="view === 'calendar' && !pending"
                    :day="day"
                    :data="data"
                    :groupby="groupby"
                    :loading="pending"
                    :start_time="startTime"
                    :end_time="endTime"
                    ref="grid"
                    @changePage="handlePageChange"
                    id="grid"
                    :suggest_slots="tab === 'favorites'"
                    :results_count="results" />
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="pending && !loadingMore">
                    <FragmentsLoading loading />
                </div>
            </template>
            <template v-else>
                <div class="empty flex flex-col items-center justify-center min-h-[50vh]" v-if="tab === 'sessions'">
                    <h2 class="mb-[2rem]">There are no sessions that match your selections</h2>
                    <p class="mb-[2rem]">Please try expanding your filtering criteria or clear your selections and start again.</p>
                    <div class="actions">
                        <button
                            class="reset-filters rounded-[50px] bg-transparent border border-[var(--magenta)] text-[var(--magenta)] px-5 py-2.5 font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-[var(--magenta)] hover:text-white"
                            @click="
                                () => {
                                    day = '2025-09-03';
                                    level = 'all';
                                    availability = 'all';
                                    time_range_val = [0, 100];
                                    tags = [];
                                    page = 1;
                                }
                            ">
                            Reset All Filters
                        </button>
                    </div>
                </div>
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="tab === 'favorites' && day === 'all'">
                    <template v-if="!loggedIn">
                        <h2>You must be logged in to view <span>My Favorites</span>.</h2>
                        <p>
                            In order to add sessions to My Favorites, you must have <nuxt-link to="/register">purchased registration</nuxt-link> for INBOUND and you must be logged
                            in. Once logged in, simply click the add to favorites icon on any session.
                        </p>
                    </template>
                    <template v-else>
                        <h2>You haven't added any sessions to <span>My Favorites</span>.</h2>
                        <p>Simply click the add to favorites icon on any session.</p>
                    </template>
                    <div class="actions">
                        <UButton v-if="!loggedIn" variant="outline" color="neutral" class="reset-filters" @click="handleLoginClick"> Login </UButton>
                        <UButton
                            variant="outline"
                            color="neutral"
                            class="reset-filters"
                            @click="
                                () => {
                                    tab = 'sessions';
                                    day = '2025-09-03';
                                    level = 'all';
                                    time_range_val = [0, 100];
                                    tags = [];
                                    page = 1;
                                }
                            ">
                            View All Sessions
                        </UButton>
                    </div>
                </div>
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="tab === 'favorites' && day !== 'all'">
                    <template v-if="!loggedIn">
                        <h2>You must be logged in to view <span>My Favorites</span>.</h2>
                        <p>
                            In order to add sessions to My Favorites, you must have <nuxt-link to="/register">purchased registration</nuxt-link> for INBOUND and you must be logged
                            in. Once logged in, simply click the add to favorites icon on any session.
                        </p>
                    </template>
                    <template v-else>
                        <h2>You haven't added any sessions to <span>My Favorites</span> for this day yet.</h2>
                        <p>
                            Please try selecting a different day. In order to add sessions to My Favorites, you must have
                            <nuxt-link to="/register">purchased registration</nuxt-link> for INBOUND and you must be logged in. Once logged in, simply click the add to favorites
                            icon on any session.
                        </p>
                    </template>
                    <div class="actions">
                        <UButton v-if="!loggedIn" variant="outline" color="neutral" class="reset-filters" @click="handleLoginClick"> Login </UButton>
                        <UButton
                            variant="outline"
                            color="neutral"
                            class="reset-filters"
                            @click="
                                () => {
                                    tab = 'sessions';
                                    day = '2025-09-03';
                                    level = 'all';
                                    time_range_val = [0, 100];
                                    tags = [];
                                    page = 1;
                                }
                            ">
                            View All Sessions
                        </UButton>
                    </div>
                </div>
                <div class="ui-region" v-if="tab === 'assistant' && loggedIn">
                    <SessionsAssistant :view="view" />
                </div>
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="tab === 'assistant' && !loggedIn">
                    <h2>You must be logged in to utilize the Assistant.</h2>
                    <p>
                        The AI Agenda Assistant is limited to registered attendees only. Please log in using your email and ticket number to use the tool. Or
                        <nuxt-link to="/register">purchase a ticket</nuxt-link> to gain access.
                    </p>
                    <div class="actions">
                        <UButton v-if="!loggedIn" variant="outline" color="neutral" class="reset-filters" @click="handleLoginClick"> Login </UButton>
                    </div>
                </div>
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="tab === 'seats'">
                    <template v-if="!loggedIn">
                        <h2>You must be logged in to view <span>My Saved Seats</span>.</h2>
                        <p>
                            In order to add sessions to My Saved Seats, you must have <nuxt-link to="/register">purchased registration</nuxt-link> for INBOUND and you must be
                            logged in. Once logged in, simply click the save a seat icon on any session.
                        </p>
                    </template>
                    <template v-else>
                        <h2>You haven't added any sessions to <span>My Saved Seats</span>.</h2>
                        <p>
                            Please try selecting a different day or adjusting your filters. In order to add a session to My Saved Seats, simply click the save a seat icon on any
                            session.
                        </p>
                    </template>
                    <div class="actions">
                        <UButton v-if="!loggedIn" variant="outline" color="neutral" class="reset-filters" @click="handleLoginClick"> Login </UButton>
                        <UButton
                            variant="outline"
                            color="neutral"
                            class="reset-filters"
                            @click="
                                () => {
                                    tab = 'sessions';
                                    day = '2025-09-03';
                                    level = 'all';
                                    time_range_val = [0, 100];
                                    tags = [];
                                    page = 1;
                                }
                            ">
                            View All Sessions
                        </UButton>
                    </div>
                </div>
            </template>
            <GlobalBackToTop />
        </div>
        <FragmentsDrawersSessionTags
            v-model:open="showTags"
            v-model:tags="tags"
            :objects="data?.total || 0"
            :advanced="false"
            :hideAdvanced="true"
            v-model:availability="availability"
            :startTime="startTime"
            :endTime="endTime"
            :year="year"
            v-model:day="day"
            v-model:seating="seating"
            v-model:time_range="time_range_val"
            v-model:skill_level="level">
        </FragmentsDrawersSessionTags>
        <FragmentsDialogsSearch v-model:open="showSearch" :options="{ requestOptions: { facetFilters: ['type:Session'] } }"></FragmentsDialogsSearch>
        <FragmentsDrawersHelp v-if="assistantHelp" name="Assistant" v-model:open="assistantHelp" />
    </div>
</template>

<style>
    /* Block root — background images and padding */
    .block-sessions-full-grid {
        --mobile-width: calc(90vw);
        position: relative;
        padding: 43px 0 28px;
    }

    /* Back-to-top positioning.
       The fixed-state button is aligned to the grid's left padding (applied to
       the viewport). At rest it falls in flow at the block's bottom — give
       the wrapper its own height so the absolute button has somewhere to land
       without overlapping the last grid row. */
    .block-sessions-full-grid .back-to-top {
        min-height: 36px;
        margin-top: 2.375rem;
    }

    .block-sessions-full-grid .back-to-top.is-fixed > button,
    .block-sessions-full-grid .back-to-top.offscreen > button {
        left: var(--box-padding-lr-m);
    }

    @media (min-width: 1024px) {
        .block-sessions-full-grid .back-to-top.is-fixed > button,
        .block-sessions-full-grid .back-to-top.offscreen > button {
            left: var(--box-padding-lr);
        }
    }

    /* When at rest (absolute), align to the wrapper's left edge which already
       sits inside the grid's padded container. */
    .block-sessions-full-grid .back-to-top:not(.is-fixed):not(.offscreen) > button {
        left: 0;
        bottom: 0;
        transition-property: opacity, transform, background-color, color;
    }

    @media (min-width: 1024px) {
        .block-sessions-full-grid {
            padding: 75px 0 28px;
            padding-top: 6.0625rem;
        }
    }

    /* Hero variant */
    .block-sessions-full-grid.is-hero {
        margin-top: calc(var(--header-height) * -1);
        padding-top: 97px;
        padding-bottom: var(--box-padding-tb-m);
    }

    @media (min-width: 1024px) {
        .block-sessions-full-grid.is-hero {
            padding-top: 150px;
            margin-bottom: 3.75rem;
        }
    }

    /* Archive-specific: copy paragraph spacing */
    .block-sessions-full-grid .copy p {
        margin-bottom: 0.5rem;
    }

    /* Controls — calendar view state */
    .block-sessions-full-grid .controls.is-cal {
        margin-bottom: 1.3125rem;
    }

    @media (min-width: 1024px) {
        .block-sessions-full-grid .controls.is-cal {
            margin-bottom: 2rem;
        }
    }

    /* Share */
    @media (min-width: 1024px) {
        .block-sessions-full-grid .share {
            margin-bottom: -0.8125rem;
        }
    }

    /* Active tags — button sizing */
    .block-sessions-full-grid .active-tags ul li button {
        font-size: calc(11 / var(--base-fs) * 1rem);
        padding: 0.3rem 0.6rem;
        letter-spacing: 0.034375rem;
    }


    /* Empty state — text styling */
    .block-sessions-full-grid .empty h2 {
        text-align: center;
        font-family: var(--font-hubspot-sans);
        font-size: 2rem;
        font-weight: 400;
        letter-spacing: 0;
        margin-bottom: 1.375rem;
        max-width: 56.25rem;
        line-height: 2rem;
        color: var(--magenta);
    }

    @media (min-width: 1024px) {
        .block-sessions-full-grid .empty h2 {
            line-height: 3rem;
        }
    }

    .block-sessions-full-grid .empty p {
        text-align: center;
        margin-bottom: 2rem;
        max-width: 42.25rem;
        font-family: var(--font-hubspot-sans);
        color: var(--magenta);
    }

    .block-sessions-full-grid .empty p a {
        text-decoration: underline;
        text-underline-offset: 0.25rem;
    }

    @media (hover: hover) {
        .block-sessions-full-grid .empty p a:hover {
            color: var(--dark-pink);
            text-decoration: none;
        }
    }

    .block-sessions-full-grid .more-filters,
    .block-sessions-full-grid .availability-select {
        color: var(--magenta);
        border-color: var(--magenta);
        border-width: 1px;
        border-radius: 50px;
        box-shadow: none;
        font-family: var(--font-hubspot-serif);
        font-size: calc(17 / var(--base-fs) * 1rem);
        line-height: 1.4;
        letter-spacing: 0.34px;
        padding: 10px 20px;
    }

    .block-sessions-full-grid .availability-select {
        padding-right: 42px;
    }

    .block-sessions-full-grid .more-filters {
        gap: 10px;
    }

    .block-sessions-full-grid .more-filters:hover,
    .block-sessions-full-grid .availability-select:hover {
        background: var(--magenta);
        color: white;
        border-color: var(--magenta);
    }

    .block-sessions-full-grid .empty h2 span {
        color: var(--dark-pink);
    }

    .block-sessions-full-grid .empty .actions {
        display: flex;
        gap: 1.5625rem;
    }

    .block-sessions-full-grid .empty button {
        width: fit-content;
    }

</style>
